import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { Control } from '@/types';

/**
 * Control status types for display
 * - NOT_STARTED: No documents uploaded, no progress (grey)
 * - INCOMPLETE: Some progress but not ready (yellow/warning)
 * - READY: All required documents uploaded, ready to share (green)
 * - SHARED: Control has been shared with auditor (blue)
 * - OVERDUE: Scheduled date has passed and no share exists (red)
 */
export type ControlDisplayStatus = 'NOT_STARTED' | 'INCOMPLETE' | 'READY' | 'SHARED' | 'OVERDUE';

interface ControlStatusBadgeProps {
  status: ControlDisplayStatus;
  size?: 'small' | 'medium';
  missingCount?: number; // Number of missing documents to display
}

export interface ControlStatusInfo {
  status: ControlDisplayStatus;
  missingCount: number;
}

/**
 * Calculate the display status for a control based on its data
 */
export function getControlDisplayStatus(control: Control): ControlStatusInfo {
  // Calculate progress to determine status
  const documentRequirements = control.documentRequirements || [];
  const requirementStatuses = control.requirementStatuses || [];
  const confirmedDocuments = control.confirmedDocuments || [];

  // Separate required and optional
  const requiredRequirements = documentRequirements.filter((req) => req.isRequired);

  // Base total: required (exclude NOT_APPLICABLE)
  const notApplicableCount = requiredRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);
    return status?.verificationMethod === 'NOT_APPLICABLE';
  }).length;

  const totalRequired = requiredRequirements.length - notApplicableCount;

  // Count handled required requirements
  const handledRequiredCount = requiredRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);

    // Skip not applicable
    if (status?.verificationMethod === 'NOT_APPLICABLE') return false;

    // Count as handled if checked on farm
    if (status?.verificationMethod === 'CHECKED_ON_FARM') return true;

    // Count as handled if document uploaded
    return confirmedDocuments.some((doc) => doc.requirementId === req.id);
  }).length;

  const missingCount = totalRequired - handledRequiredCount;

  // If control has an active share, it's "Delad" (Shared)
  if (control.hasActiveShare) {
    return { status: 'SHARED', missingCount };
  }

  // Check if overdue: scheduled date has passed and not completed
  if (control.scheduledDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduledDate = new Date(control.scheduledDate);
    scheduledDate.setHours(0, 0, 0, 0);

    if (scheduledDate < today && control.status !== 'COMPLETED') {
      return { status: 'OVERDUE', missingCount };
    }
  }

  // If no requirements, consider it ready
  if (totalRequired === 0) {
    return { status: 'READY', missingCount: 0 };
  }

  // Calculate percentage
  const percentage = Math.round((handledRequiredCount / totalRequired) * 100);

  if (percentage === 0) {
    return { status: 'NOT_STARTED', missingCount };
  } else if (percentage < 100) {
    return { status: 'INCOMPLETE', missingCount };
  } else {
    return { status: 'READY', missingCount: 0 };
  }
}

const ControlStatusBadge: React.FC<ControlStatusBadgeProps> = ({ status, size = 'small', missingCount }) => {
  const theme = useTheme();

  const getStatusConfig = () => {
    switch (status) {
      case 'NOT_STARTED':
        return {
          label: 'Ej påbörjad',
          bgcolor: theme.palette.info.pastel,
          borderColor: theme.palette.info.light,
          color: theme.palette.info.dark,
        };
      case 'INCOMPLETE':
        return {
          label: missingCount && missingCount > 0 ? `${missingCount} saknas` : 'Inkomplett',
          bgcolor: theme.palette.warning.pastel,
          borderColor: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        };
      case 'READY':
        return {
          label: 'Redo',
          bgcolor: theme.palette.success.pastel,
          borderColor: theme.palette.success.light,
          color: theme.palette.success.dark,
        };
      case 'SHARED':
        return {
          label: 'Länk skapad',
          bgcolor: theme.palette.secondary.pastel,
          borderColor: theme.palette.secondary.light,
          color: theme.palette.secondary.dark,
        };
      case 'OVERDUE':
        return {
          label: 'Försenad',
          bgcolor: theme.palette.warning.pastel,
          borderColor: theme.palette.warning.light,
          color: theme.palette.warning.dark,
        };
      default:
        return {
          label: 'Okänd',
          bgcolor: theme.palette.grey[100],
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
        };
    }
  };

  const config = getStatusConfig();

  return (
    <Chip
      label={config.label}
      size={size}
      sx={{
        bgcolor: config.bgcolor,
        border: 'none',
        color: config.color,
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: size === 'small' ? '0.8125rem' : '0.875rem',
        height: size === 'small' ? 32 : 36,
        borderRadius: '50px',
        px: 0.5,
        '& .MuiChip-label': {
          px: 1.5,
        },
      }}
    />
  );
};

export default ControlStatusBadge;
